import { ThemeEvents } from '@theme/events';

/**
 * Mobile bottom navigation behaviour.
 *
 * - Hides when the visitor scrolls down and shows again when they scroll up.
 *   The script only toggles `data-scroll-hidden`; the movement itself is a CSS
 *   transform (see sections/bottom-nav.liquid), so layout is never affected.
 * - Keeps the cart badge in sync with the theme's cart events.
 */

const MOBILE_QUERY = '(max-width: 749px)';

/** Continuous movement (px) in one direction required before the bar reacts. */
const SCROLL_THRESHOLD = 10;

/** Within this distance (px) of the top of the page the bar always stays visible. */
const TOP_ZONE = 64;

/** Within this distance (px) of the page bottom the bar is always visible (its space is reserved by the body padding). */
const BOTTOM_ZONE = 24;

class MobileBottomNav extends HTMLElement {
  #mediaQuery = window.matchMedia(MOBILE_QUERY);

  /** @type {AbortController | null} */
  #abortController = null;

  #frame = 0;
  #lastY = 0;
  #travel = 0;
  #hidden = false;

  /** @type {HTMLElement | null} */
  #badge = null;
  #count = 0;

  connectedCallback() {
    this.#badge = this.querySelector('[data-cart-count]');
    this.#count = Number(this.#badge?.dataset.cartCount) || 0;
    this.#lastY = window.scrollY;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.#mediaQuery.addEventListener('change', this.#resetScrollState, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  /* ---------------------------------------------------------------- scroll */

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    this.#frame = 0;
    if (!this.#mediaQuery.matches) return;

    // scrollY can be negative / past the end while iOS rubber-bands, which is handled by the zones below.
    const y = window.scrollY;
    const maxY = document.documentElement.scrollHeight - window.innerHeight;
    const delta = y - this.#lastY;
    this.#lastY = y;

    if (y <= TOP_ZONE || y >= maxY - BOTTOM_ZONE) {
      this.#travel = 0;
      this.#setHidden(false);
      return;
    }

    if (delta === 0) return;

    // Changing direction restarts the dead-zone count, so tiny wobbles never toggle the bar.
    if (Math.sign(delta) !== Math.sign(this.#travel)) this.#travel = 0;
    this.#travel += delta;

    if (this.#travel >= SCROLL_THRESHOLD) {
      this.#setHidden(true);
    } else if (this.#travel <= -SCROLL_THRESHOLD) {
      this.#setHidden(false);
    }
  };

  /** @param {boolean} hidden */
  #setHidden(hidden) {
    if (hidden === this.#hidden) return;
    this.#hidden = hidden;
    this.dataset.scrollHidden = String(hidden);
  }

  #resetScrollState = () => {
    this.#lastY = window.scrollY;
    this.#travel = 0;
    this.#setHidden(false);
  };

  #onFocusIn = () => {
    this.#travel = 0;
    this.#setHidden(false);
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    // Restored from the back/forward cache: the badge and scroll position may be stale.
    if (!event.persisted) return;
    this.#resetScrollState();
    this.#syncCountFromServer();
  };

  /* ------------------------------------------------------------- cart badge */

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    const data = event.detail?.data;
    if (data?.didError) return;

    const itemCount = data?.itemCount;

    if (typeof itemCount === 'number' && Number.isFinite(itemCount)) {
      // Same rule as the theme's cart icon: product forms report the quantity that was ADDED,
      // every other source (cart drawer, cart page, ...) reports the new cart total.
      this.#renderCount(data.source === 'product-form-component' ? this.#count + itemCount : itemCount);
    } else {
      this.#syncCountFromServer();
    }
  };

  async #syncCountFromServer() {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return;

      const cart = await response.json();
      this.#renderCount(cart.item_count);
    } catch (_) {
      // Keep whatever the badge currently shows.
    }
  }

  /** @param {number} total */
  #renderCount(total) {
    const count = Math.max(0, Math.trunc(total) || 0);
    this.#count = count;

    if (!this.#badge) return;
    this.#badge.dataset.cartCount = String(count);
    this.#badge.textContent = count > 99 ? '99+' : String(count);
    this.#badge.hidden = count === 0;
  }
}

if (!customElements.get('mobile-bottom-nav')) {
  customElements.define('mobile-bottom-nav', MobileBottomNav);
}
